import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import icfLogo from '../assets/images/marcas/LOGOTIPO-ZOOM.png';

const Header = ({ navigationItems, activeSection, scrollToSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (itemId) => {
    if (scrollToSection) {
      scrollToSection(itemId);
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${
      scrollY > 50 
        ? 'bg-green-700 shadow-xl shadow-green-900/30' 
        : 'bg-orange-600'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      style={{ maxHeight: '90px' }}>
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <img 
                src={icfLogo} 
                alt="Campo Fresco ICF Logo" 
                className="object-contain drop-shadow-lg"
                style={{ 
                  width: '180px', 
                  height: '110px', 
                  position: 'relative', 
                  top: '-7px'
                }}
              />
            </div>
          </div>         
          
          {/* Desktop Navigation - Solo visible en pantallas grandes */}
          <div className="hidden xl:flex space-x-8">
            {navigationItems.map((item, index) => (
              <div key={item.id} className="relative group">
                <button 
                  onClick={() => handleNavigation(item.id)}
                  className={`font-medium transition-all duration-300 relative group ${
                    activeSection === item.id
                      ? 'text-yellow-200 font-semibold'
                      : 'text-white hover:text-yellow-200'
                  }`}
                >
                  {item.name}
                  <span className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 ${
                    activeSection === item.id 
                      ? 'w-full bg-yellow-300'
                      : 'w-0 group-hover:w-full bg-yellow-200'
                  }`}></span>
                </button>
                {/* Submenu para desktop */}
                {item.subItems && item.subItems.length > 0 && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 transition-all z-50 border border-gray-100">
                    {item.subItems.map(sub => (
                      <button
                        key={sub.id}
                        onClick={() => handleNavigation(sub.id)}
                        className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-green-100 hover:text-green-700 rounded-lg transition-all"
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* iPad/Tablet Navigation - Horizontal compacta */}
          <div className="hidden md:flex xl:hidden space-x-2 lg:space-x-4">
            {navigationItems.slice(0, 5).map((item, index) => (
              <div key={item.id} className="relative group">
                <button 
                  onClick={() => handleNavigation(item.id)}
                  className={`text-sm lg:text-base font-medium transition-all duration-300 relative group px-2 lg:px-3 py-1 rounded ${
                    activeSection === item.id
                      ? 'text-yellow-200 font-semibold bg-white/10'
                      : 'text-white hover:text-yellow-200 hover:bg-white/10'
                  }`}
                >
                  {item.name}
                </button>
                {/* Submenu simplificado para iPad */}
                {item.subItems && item.subItems.length > 0 && (
                  <div className="absolute left-0 mt-2 w-44 bg-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 transition-all z-50 border border-gray-100">
                    {item.subItems.map(sub => (
                      <button
                        key={sub.id}
                        onClick={() => handleNavigation(sub.id)}
                        className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-green-100 hover:text-green-700 rounded-lg transition-all"
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {/* Botón "Más" si hay más de 5 items */}
            {navigationItems.length > 5 && (
              <div className="relative group">
                <button className="text-sm lg:text-base font-medium text-white hover:text-yellow-200 px-2 lg:px-3 py-1 rounded hover:bg-white/10 transition-all">
                  Más
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 transition-all z-50 border border-gray-100">
                  {navigationItems.slice(5).map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.id)}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-green-100 hover:text-green-700 rounded-lg transition-all"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button - Solo en móviles */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-all duration-300 shadow-lg backdrop-blur-sm"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Solo en móviles */}
      {isMenuOpen && (
        <div className={`md:hidden backdrop-blur-md border-t border-white/20 shadow-xl ${
          scrollY > 50 ? 'bg-green-800/95' : 'bg-orange-700/95'
        }`}>
          <div className="px-4 py-6 space-y-4">
            {navigationItems.map((item, index) => (
              <div key={item.id}>
                <button 
                  onClick={() => handleNavigation(item.id)}
                  className={`block w-full text-left font-medium py-3 px-4 rounded-lg transition-all duration-300 ${
                    activeSection === item.id 
                      ? 'text-yellow-200 bg-white/10 border-l-4 border-yellow-300 font-semibold'
                      : 'text-white hover:text-yellow-200 hover:bg-white/10'
                  }`}
                >
                  {item.name}
                </button>
                {/* Submenu para móvil */}
                {item.subItems && item.subItems.length > 0 && (
                  <div className="pl-4 mt-1 space-y-1">
                    {item.subItems.map(sub => (
                      <button
                        key={sub.id}
                        onClick={() => handleNavigation(sub.id)}
                        className="block w-full text-left py-2 px-4 rounded-lg text-green-100 hover:bg-green-900/30 hover:text-yellow-200 transition-all"
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;